import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import BeforeAfter from "./BeforeAfter";
import Photo from "./Photo";
import Reveal from "./Reveal";
import useI18n from "@/i18n";
import { SOIN_KEYS } from "@/i18n/config";

const IMAGES = {
    levres: ["/images/soins/levres-avant.jpg", "/images/soins/levres-apres.jpg"],
    melasma: ["/images/soins/melasma-avant.jpg", "/images/soins/melasma-apres.jpg"],
    cheveux: ["/images/soins/cheveux-avant.jpg", "/images/soins/cheveux-apres.jpg"],
};

export default function Soins() {
    const { t, soins, toSoin } = useI18n();
    const illus = t.soinsSection.illustrations;

    return (
        <section id="soins" className="halo halo-left grad-up scroll-mt-24 overflow-hidden py-16 md:py-24" data-testid="soins-section">
            <div className="mx-auto max-w-[1080px] px-5">
                <Reveal>
                    <p className="eyebrow">{t.soinsSection.eyebrow}</p>
                    <h2 className="text-3xl text-terrat md:text-4xl">{t.soinsSection.h2}</h2>
                    <hr className="rule-gold mt-4 w-16" />
                    <p className="mt-4 max-w-[52ch] text-lg text-ink2">{t.soinsSection.lead}</p>
                </Reveal>
                <Reveal delay={0.1}>
                    <Photo
                        file="salle.jpg"
                        alt={t.soinsSection.photoAlt}
                        legend={t.soinsSection.photoLegend}
                        ratioClass="aspect-[4/3] rounded-[14px] md:aspect-[16/9]"
                        className="mt-10"
                    />
                </Reveal>

                <div className="mt-12 grid gap-6 md:grid-cols-2">
                    {t.soinsSection.familles.map(([titre, items, key], i) => (
                        <Reveal key={key} delay={i * 0.05}>
                            <div className="card card-motif h-full p-6" data-testid={`soin-famille-${key}`}>
                                <h3 className="text-xl text-olivet">{titre}</h3>
                                <hr className="rule-gold mt-3 w-12" />
                                <ul className="mt-3 space-y-1.5">
                                    {items.map((item) => (
                                        <li key={item} className="flex gap-2.5">
                                            <span aria-hidden="true" style={{ color: "var(--c-terra)" }}>·</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    to={toSoin(key)}
                                    className="link-underline mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-terrat"
                                    data-testid={`soin-lien-${key}`}
                                >
                                    {t.common.readMore}
                                    <ArrowRight size={14} aria-hidden="true" />
                                </Link>
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal>
                    <div className="mt-14" data-testid="soins-pages-detaillees">
                        <h3 className="text-xl text-olivet">{t.soinsSection.detailTitle}</h3>
                        <hr className="rule-gold mt-3 w-12" />
                        <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2.5">
                            {SOIN_KEYS.map((key) => (
                                <li key={key}>
                                    <Link
                                        to={toSoin(key)}
                                        className="link-underline inline-flex items-center gap-1.5 text-sm font-bold text-terrat"
                                        data-testid={`soin-page-lien-${key}`}
                                    >
                                        {soins[key].nav}
                                        <ArrowRight size={13} aria-hidden="true" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>

                <div className="mt-20" data-testid="soins-illustrations">
                    <Reveal>
                        <p className="eyebrow">{illus.eyebrow}</p>
                        <h3 className="mt-4 text-2xl text-terrat md:text-3xl">{illus.h3}</h3>
                        <p className="mt-4 max-w-[62ch] text-ink2">{illus.lead}</p>
                    </Reveal>
                    <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
                        {Object.keys(IMAGES).map((id, i) => (
                            <Reveal key={id} delay={i * 0.08}>
                                <BeforeAfter
                                    id={id}
                                    title={illus.items[id].title}
                                    note={illus.items[id].note}
                                    beforeSrc={IMAGES[id][0]}
                                    afterSrc={IMAGES[id][1]}
                                    beforeAlt={illus.items[id].beforeAlt}
                                    afterAlt={illus.items[id].afterAlt}
                                />
                            </Reveal>
                        ))}
                    </div>
                    <Reveal>
                        <p
                            className="mt-8 max-w-[72ch] text-xs"
                            style={{ color: "var(--c-ink2)" }}
                            data-testid="illustrations-disclaimer"
                        >
                            {illus.disclaimer}
                        </p>
                    </Reveal>
                </div>

                <Reveal>
                    <p className="mt-12 max-w-[72ch] text-sm italic text-ink2">
                        {t.soinsSection.footnote}
                    </p>
                </Reveal>
            </div>
        </section>
    );
}
